/**
 * Per-AP radio overrides -- server-state hooks and response types (device detail > Radios).
 *
 * Backend: api/v2/radios.py + services/radio_overrides.py (OP#1760). Precedence per band and
 * field: AP override > site setting (Wireless > Radio Settings) > default. On 2.4 GHz the
 * default channel width is 20 MHz. Reads are site_operator+; writes are org_admin+ server-side
 * (RADIO_OVERRIDE_WRITE_ROLE) -- the RoleGates in components/device/DeviceRadiosSection.tsx
 * must match.
 *
 * A write is previewed first (dry_run) and a change that alters the AP's config needs
 * confirm_outage: true -- every uCentral push costs the AP about 4 minutes without Wi-Fi.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import { qk } from "./queryKeys";
import { useSession } from "@/stores/session";

/* ---- types ------------------------------------------------------------------------------ */

export type RadioBand = "2G" | "5G" | "6G";
export type RadioField = "channel" | "channel_width" | "tx_power";
/** channel / tx_power: a number or "auto"; channel_width: MHz. null = not set. */
export type RadioValue = number | "auto" | null;
export type RadioSource = "ap" | "site" | "default";

export interface DeviceRadioField {
  /** The value this AP should run (AP > site > default). null = firmware default. */
  effective: RadioValue;
  source: RadioSource;
  /** What the AP's config carries right now. */
  live: RadioValue;
  /** The site tier: the site's saved value, or the default when the site never set it. */
  site_value: RadioValue;
  site_set: boolean;
  /** This AP's own value, when it overrides the field. */
  override: RadioValue;
  overridden: boolean;
}

export interface DeviceRadioBand {
  band: RadioBand;
  label: string;
  in_config: boolean;
  /** null = capabilities unknown. */
  supported: boolean | null;
  /** Widths (MHz) this AP can use on the band (capability-driven). */
  widths: number[];
  widths_source: "capabilities" | "default" | "unsupported";
  /** Channel + width pinned by the site's Wi-Fi mesh on this AP (TX power still editable). */
  mesh_pinned: boolean;
  /** Width the firmware uses when none is rendered (uCentral schema default). */
  schema_default_width: number;
  fields: Record<RadioField, DeviceRadioField>;
}

export interface DeviceRadios {
  serial: string;
  site_id: number | null;
  owgw_available: boolean;
  outage_minutes: number;
  bands: DeviceRadioBand[];
}

/** Body: field absent = unchanged; null = revert to site; value = override on this AP. */
export type RadioOverrideBody = Partial<Record<RadioField, RadioValue>> & {
  dry_run?: boolean;
  confirm_outage?: boolean;
};

export type RadioOverrideOutcome =
  | "pending_confirmation"
  | "preview"
  | "unchanged"
  | "applied"
  | "applied_with_warnings"
  | "pending"
  | "rejected";

/** One band's radio keys as uCentral spells them ("channel", "channel-width", "tx-power"). */
export type RadioSnapshot = Partial<Record<"channel" | "channel-width" | "tx-power", number | string>>;

export interface RadioOverridePreview {
  before: RadioSnapshot;
  after: RadioSnapshot;
  changed: boolean;
  outage_minutes: number;
  side_effects: string[];
  overrides_before: Partial<Record<RadioField, RadioValue>>;
  overrides_after: Partial<Record<RadioField, RadioValue>>;
}

export interface RadioOverrideResult {
  outcome: RadioOverrideOutcome;
  band: RadioBand;
  preview: RadioOverridePreview;
  changed: boolean;
  /** Device warnings for applied_with_warnings (redacted server-side). */
  warnings: string[];
  /** Rejection / warning sentence from the device, when any. */
  reason: string | null;
  overrides: Partial<Record<RadioField, RadioValue>>;
}

/* ---- hooks ------------------------------------------------------------------------------ */

/** GET /api/v2/devices/{serial}/radios -- the AP's Radios panel. */
export function useDeviceRadios(serial: string, enabled = true) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.radios(orgId, serial),
    queryFn: ({ signal }) =>
      api.get<DeviceRadios>(`/devices/${encodeURIComponent(serial)}/radios`, undefined, signal),
    enabled: enabled && !!serial,
  });
}

/** PUT /api/v2/devices/{serial}/radios/{band}/overrides -- preview (dry_run) or apply. */
export function useSetDeviceRadioOverrides(serial: string) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
  return useMutation({
    mutationFn: ({ band, body }: { band: RadioBand; body: RadioOverrideBody }) =>
      api.put<RadioOverrideResult>(
        `/devices/${encodeURIComponent(serial)}/radios/${band}/overrides`, body),
    onSuccess: (res) => {
      if (res.outcome === "preview") return;
      qc.invalidateQueries({ queryKey: qk.devices.radios(orgId, serial) });
      // The site Radio Settings page lists which APs override it.
      qc.invalidateQueries({ queryKey: qk.sites.radios(orgId, siteId) });
    },
  });
}
